var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import clsx from 'clsx';
import { numStringToCss, contrastColor, numBoolToCss, prop } from '@bit/daviidli.corn-ui.utils';
const getSize = (scale = 1) => ({ Size }) => {
    let em = 1;
    switch (Size) {
        case 'xs':
            em = 0.7;
            break;
        case 'sm':
            em = 0.8;
            break;
        case 'md':
            em = 1;
            break;
        case 'lg':
            em = 1.5;
            break;
        default:
            break;
    }
    return `${scale * em}em`;
};
const StyledButton = styled.button `
	transition: all 0.3s ease;

	position: relative;
	overflow: hidden;
	width: ${prop('Width')};
	height: ${prop('Height')};

	display: inline-flex;
	justify-content: center;
	align-items: center;

	border: none;
	border-radius: ${prop('Round')};
	padding: ${getSize(0.6)};
	font-size: ${getSize(1)};
	background-color: ${prop('Color')};
	color: ${p => {
    if (p.Disabled)
        return '#888';
    if (p.TextColor)
        return p.TextColor;
    return contrastColor(p.Color, '#fff', '#000');
}};
	box-shadow: ${p => (p.Raised
    ? '0px 0px 8px -2px rgba(0, 0, 0, 0.5), 0px 0px 8px -2px rgba(0, 0, 0, 0.1)'
    : 'none')};

	${p => {
    if (p.Circle) {
        return css `
				border-radius: 100%;

				&::before {
					content: '';
					float: left;
					padding-top: 100%;
				}
			`;
    }
    return '';
}}

	margin-top: ${p => (p.HoverRaised ? '4px' : '0px')};
	margin-bottom: 0px;

	cursor: ${p => (p.Disabled ? 'not-allowed' : 'pointer')};
	-webkit-tap-highlight-color: transparent;

	&::after {
		content: "";
		background-color: ${p => (p.Disabled ? 'rgba(0,0,0,0.3)' : 'transparent')};
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		transition: background-color 0.3s ease;
	}

	&:hover {
		&::after {
			background-color: ${p => (p.Disabled ? 'rgba(0,0,0,0.3)' : p.HoverColor)};
		}

		${p => (p.HoverRaised && !p.Disabled
    ? css `
				margin-top: 0px;
				margin-bottom: 4px;
				box-shadow: 0px 5px 8px -4px rgba(0, 0, 0, 0.4),
					0px 7px 12px 0px rgba(0, 0, 0, 0.2);
			` : '')}
	}

	&:focus {
		outline: none;
		box-shadow: 0px 0px 3px 1px ${prop('FocusColor')};
	}

	transform: ${p => (p.Click ? `scale(${p.ClickScale}, ${p.ClickScale})` : 'scale(1, 1)')};
`;
const Button = (props) => {
    const { children, className, onClick, corners, size, disabled, raised, width, height, color, focusColor, textColor, hoverColor, hoverRaised, clickScale } = props, others = __rest(props, ["children", "className", "onClick", "corners", "size", "disabled", "raised", "width", "height", "color", "focusColor", "textColor", "hoverColor", "hoverRaised", "clickScale"]);
    const [click, setClick] = useState(false);
    return (React.createElement(StyledButton, Object.assign({}, others, { className: clsx('corn-button', disabled && 'disabled', className), onClick: onClick, Round: numBoolToCss(corners, 'round', '0.5em'), Circle: corners === 'circle', Size: size, Disabled: disabled, Raised: raised, Width: numStringToCss(width), Height: numStringToCss(height), Color: color, FocusColor: focusColor, TextColor: textColor, HoverColor: hoverColor, HoverRaised: hoverRaised, Click: click, ClickScale: clickScale, onMouseDown: () => setClick(true), onMouseUp: () => setClick(false), onTouchStart: () => setClick(true), onTouchEnd: () => setClick(false) }), children));
};
Button.propTypes = {
    onClick: PropTypes.func,
    children: PropTypes.node,
    corners: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['normal', 'round', 'circle'])]),
    color: PropTypes.string,
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
    disabled: PropTypes.bool,
    raised: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
    focusColor: PropTypes.string,
    textColor: PropTypes.string,
    hoverColor: PropTypes.string,
    hoverRaised: PropTypes.bool,
    clickScale: PropTypes.number
};
Button.defaultProps = {
    corners: 'round',
    color: '#ddd',
    size: 'md',
    disabled: false,
    raised: false,
    width: 'auto',
    height: 'auto',
    focusColor: '#4d90fe',
    hoverColor: 'rgba(255, 255, 255, 0.2)',
    hoverRaised: false,
    clickScale: 0.97
};
export default Button;
